import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { USE_MOCK_DATA, useAppContext, EvidenceItem, Witness, Matter } from "../../../lib/mockState";
import { enumLabel } from "../../../lib/enumLabels";
import { MatterShellBanner } from "../../../components/matter/MatterShellBanner";
import { useFirmDocuments } from "../../../hooks/useLiveSurfaceRecords";
import { buildCaseBundlePlan } from "../../../lib/onboardingRemediation";
import {
  addCaseExpenseCallable,
  addCaseHearingCallable,
  closeCaseMatterCallable,
  createCaseAppealMatterCallable,
  updateCaseEvidenceStateCallable,
  uploadCaseEvidenceDocumentCallable,
} from "../../../lib/phase45Callables";
import { buildExportBundleCallable, requestExportCallable } from "../../../lib/phase75Callables";
import { 
  Scale, 
  Bot, 
  Upload, 
  ShieldAlert, 
  ChevronRight, 
  Check, 
  PlusCircle, 
  BookOpen, 
  User, 
  Users, 
  Clock, 
  Calendar,
  Layers,
  FileCheck,
  Award,
  Pin,
  ClipboardList,
  Mail,
  AlertTriangle,
  Flame,
  CornerDownRight,
  Sparkles,
  Download,
  Shield,
  Search,
  CheckCircle2,
  FileSpreadsheet,
  FileSignature,
  History,
  TrendingDown,
  ChevronDown,
  Filter,
  ArrowRight,
  BrainCircuit,
  Lock,
  FileText,
  BadgeAlert,
  Fingerprint,
  RotateCcw,
  Zap,
  BookMarked
} from "lucide-react";

export const CasePage: React.FC = () => {
  const { 
    role, 
    lang, 
    activeMatterId, 
    matters, 
    clients,
    validateEvidence, 
    uploadEvidence, 
    addWitness,
    alerts,
    currentRoute,
    setActiveMatterId,
    setCurrentRoute,
    openMatterDraft,
    writeLogs
  } = useAppContext();
  const { caseId } = useParams();

  const isAr = lang === "ar";
  const firmDocuments = useFirmDocuments();
  
  const fallbackClient = clients[0];
  const routeFallbackMatter: Matter | undefined = caseId
    ? {
        matterId: caseId,
        id: caseId,
        title: `Case Matter ${caseId}`,
        matterSpecies: "CASE",
        status: "active",
        lifecycle: "INTAKE",
        clientId: fallbackClient?.clientId ?? "unlinked-client",
        clientName: fallbackClient?.name ?? "Client record pending",
        leadLawyer: role,
        assignedTeam: [],
        jurisdiction: "DIFC Courts",
        solClockDays: 42,
        damagesClaimed: "AED 3,600,050",
        nextEvent: "Directions hearing",
        nextEventDate: "2026-05-28T10:00:00+04:00",
        riskScore: "Medium",
        wipValue: 0,
        arValue: 0,
        lastActivity: "Route hydration fallback",
        flags: ["Route-hydrated shell"],
        evidence: [
          {
            id: `${caseId}-ev-1`,
            name: "Signed project variation log",
            type: "Invoice",
            uploadedAt: new Date().toISOString(),
            status: "VALIDATED",
            size: "1.2 MB",
            entities: [fallbackClient?.name ?? "Client", "Gulf General Construction Co."],
            datesMentioned: ["2026-04-24", "2026-04-29"],
            admissibilityFlags: ["Certified copy", "Timeline anchor"],
            excerpt: "Variation log confirms revised delivery milestones and ties the delay sequence to signed approvals.",
            hasAiInsight: true,
          },
        ],
        witnesses: [
          {
            witnessId: `${caseId}-wit-1`,
            name: "Operations Director",
            role: "FACT",
            side: "PLAINTIFF",
            credibility: "HIGH",
            testimonySummary: "Directly observed the delayed delivery chain and authenticated the notice exchanges.",
            contradictionCount: 0,
            crossExamQuestions: [
              "When was the first delay notice received?",
              "Which revised milestone was formally approved?",
            ],
          },
        ],
        obligations: [],
        redlineRounds: [],
        riskRegister: [],
      }
    : undefined;

  // Find case matter
  const matter = matters.find((m) => (m.matterId === activeMatterId || m.id === activeMatterId) && m.matterSpecies === "CASE")
    || matters.find((m) => caseId && (m.matterId === caseId || m.id === caseId) && m.matterSpecies === "CASE")
    || matters.find((m) => m.matterSpecies === "CASE")
    || routeFallbackMatter;

  if (!matter) {
    return (
      <div className={`p-4 flex-1 overflow-y-auto bg-slate-55/10 ${isAr ? "text-right" : ""}`} dir={isAr ? "rtl" : "ltr"}>
        <MatterShellBanner species="CASE" />
        <div data-testid="case-workspace-empty" className="mt-4 rounded-xl border border-dashed border-slate-200 bg-white p-8 text-center text-sm font-semibold text-slate-500">
          {isAr ? "لا توجد قضية محمّلة لهذه الصفحة بعد." : "No case matter is loaded for this workspace yet."}
        </div>
      </div>
    );
  }

  const caseAlerts = alerts.filter(a => a.matterId === matter.matterId && a.status === "UNRESOLVED");

  // Tab state
  const [activeTab, setActiveTab] = useState<"Overview" | "Chronicle" | "Evidence" | "Hearings" | "Expenses" | "Witnesses" | "Documents" | "Intelligence">("Overview");

  // Filter & Search states
  const [evidenceSearch, setEvidenceSearch] = useState("");
  const [evidenceFilter, setEvidenceFilter] = useState<string>("ALL");
  const [witnessSearch, setWitnessSearch] = useState("");
  const [witnessFilter, setWitnessFilter] = useState<string>("ALL");

  // Dynamic Intelligence Panel states
  const [focusedScenario, setFocusedScenario] = useState<string>("sc-claim-strength");
  const [activeFindingMenu, setActiveFindingMenu] = useState<string | null>(null);

  // Interaction States
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [newDocName, setNewDocName] = useState("");
  const [newDocType, setNewDocType] = useState<EvidenceItem["type"]>("Expert Report");
  const [newDocExcerpt, setNewDocExcerpt] = useState("");
  const [selectedUploadFileName, setSelectedUploadFileName] = useState("");
  const [chronicleEntry, setChronicleEntry] = useState("");
  const [chronicleStatus, setChronicleStatus] = useState<string | null>(null);
  
  const [showWitnessDrawer, setShowWitnessDrawer] = useState(false);
  const [newWitName, setNewWitName] = useState("");
  const [newWitRole, setNewWitRole] = useState<Witness["role"]>("FACT");
  const [newWitSide, setNewWitSide] = useState<Witness["side"]>("PLAINTIFF");

  // Bundle compilation simulator state
  const [compilingBundle, setCompilingBundle] = useState(false);
  const [bundleDownloadReady, setBundleDownloadReady] = useState(false);
  const [bundleExportId, setBundleExportId] = useState<string | null>(null);
  const [persistenceStatus, setPersistenceStatus] = useState<string | null>(null);

  // Collapsible state for Virtual Folders
  const [foldersOpen, setFoldersOpen] = useState({
    pleadings: true,
    exhibits: true,
    internal: true,
    repository: true
  });

  // Intel Interactive metrics
  const [confidenceScore, setConfidenceScore] = useState(82);
  const [evidenceMaturity, setEvidenceMaturity] = useState(89);

  // Simulate file upload
  const handleFileUploadMock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDocName.trim()) return;
    setPersistenceStatus("Uploading evidence...");
    try {
      const result = await uploadCaseEvidenceDocumentCallable({
        clientId: matter.clientId,
        matterId: matter.matterId,
        name: newDocName,
        type: newDocType,
        excerpt: newDocExcerpt || "This report details delayed structural frames delivered outside optimal DIFC compliance metrics.",
        size: `${(Math.random() * 5 + 1).toFixed(1)} MB`,
      });
      uploadEvidence(matter.matterId, {
        id: result.data.documentId,
        name: newDocName,
        type: newDocType,
        excerpt: newDocExcerpt || "This report details delayed structural frames delivered outside optimal DIFC compliance metrics.",
        size: `${(Math.random() * 5 + 1).toFixed(1)} MB`,
        status: result.data.evidenceState as EvidenceItem["status"],
      });
      setPersistenceStatus(`Evidence persisted: ${result.data.documentId}`);
    } catch (error) {
      uploadEvidence(matter.matterId, {
        name: newDocName,
        type: newDocType,
        excerpt: newDocExcerpt || "This report details delayed structural frames delivered outside optimal DIFC compliance metrics.",
        size: `${(Math.random() * 5 + 1).toFixed(1)} MB`
      });
      setPersistenceStatus(error instanceof Error ? error.message : "Evidence saved locally; server persistence failed.");
    }
    setNewDocName("");
    setNewDocExcerpt("");
    setSelectedUploadFileName("");
    setShowUploadModal(false);
    writeLogs(`${role} User`, "EVIDENCE_UPLOADED", `Uploaded dynamic exhibit: ${newDocName}`);
  };

  const saveChronicleEntry = () => {
    if (!chronicleEntry.trim()) return;
    const nextStatus = `Chronicle entry saved to ${matter.matterId}: ${chronicleEntry.trim().slice(0, 72)}`;
    setChronicleStatus(nextStatus);
    writeLogs(`${role} User`, "CHRONICLE_ENTRY_SAVED", nextStatus, matter.matterId);
    setChronicleEntry("");
  };

  // Simulate witness register
  const handleWitnessAddMock = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newWitName.trim()) return;
    addWitness(matter.matterId, {
      name: newWitName,
      role: newWitRole,
      side: newWitSide,
      credibility: "MEDIUM",
      testimonySummary: "Direct observer regarding the material deliveries timeline and subsequent written notifications.",
      crossExamQuestions: [
        "What specific protocol determined your milestone sign-offs prior to April 24?",
        "Why was no formal registry amendment logged during the delay periods?",
        "To what degree did regional custom blockages impact your direct logistical pathway?"
      ]
    });
    setNewWitName("");
    setShowWitnessDrawer(false);
    writeLogs(`${role} User`, "WITNESS_REGISTERED", `Onboarded witness depositor: ${newWitName}`);
  };

  const caseBundlePlan = buildCaseBundlePlan(matter, firmDocuments.records);

  // Compile PDF bundle
  const triggerBundleCompilation = async () => {
    setCompilingBundle(true);
    setBundleDownloadReady(false);
    setBundleExportId(null);
    if (caseBundlePlan.readiness === "BLOCKED") {
      setCompilingBundle(false);
      setPersistenceStatus(caseBundlePlan.blockers.join(" "));
      return;
    }
    if (USE_MOCK_DATA) {
      setBundleDownloadReady(true);
      setBundleExportId(caseBundlePlan.bundleId);
      setCompilingBundle(false);
      setPersistenceStatus(`Bundle manifest ready: ${caseBundlePlan.bundleId} · ${caseBundlePlan.sealedHash}`);
      writeLogs(`${role} User`, "CASE_BUNDLE_COMPILED", `Compiled deterministic case bundle manifest for ${matter.matterId}: ${caseBundlePlan.sealedHash}.`);
      return;
    }
    try {
      const requested = await requestExportCallable({
        exportType: "EVIDENCE_BUNDLE",
        clientId: matter.clientId,
        matterId: matter.matterId,
        scope: `case-bundle:${caseBundlePlan.bundleId}:${caseBundlePlan.sealedHash}`,
      });
      const built = await buildExportBundleCallable({ exportId: requested.data.exportId });
      setBundleDownloadReady(built.data.status === "READY");
      setBundleExportId(built.data.exportId);
      setPersistenceStatus(`Bundle ${built.data.status.toLowerCase()}: ${built.data.exportId}`);
      writeLogs(`${role} User`, "CASE_BUNDLE_COMPILED", `Compiled export bundle ${built.data.exportId} for ${matter.matterId}.`);
    } catch (error) {
      setPersistenceStatus(error instanceof Error ? error.message : "Unable to compile case bundle.");
    } finally {
      setCompilingBundle(false);
    }
  };

  const persistHearing = async () => {
    setPersistenceStatus("Saving hearing...");
    try {
      const result = await addCaseHearingCallable({
        clientId: matter.clientId,
        matterId: matter.matterId,
        title: "Directions hearing",
        hearingDate: "2026-05-28T10:00:00+04:00",
        forum: matter.jurisdiction || "DIFC Courts",
        notes: "Hearing created from Phase 4 case workspace persistence slice.",
      });
      setPersistenceStatus(`Hearing persisted: ${result.data.hearingId}`);
    } catch (error) {
      setPersistenceStatus(error instanceof Error ? error.message : "Unable to persist hearing.");
    }
  };

  const persistExpense = async () => {
    setPersistenceStatus("Posting expense...");
    try {
      const result = await addCaseExpenseCallable({
        clientId: matter.clientId,
        matterId: matter.matterId,
        label: "Court filing fees",
        amount: 4200,
        currency: "AED",
        recoverability: "Recoverable",
      });
      setPersistenceStatus(`Expense posted: ${result.data.expenseId}`);
    } catch (error) {
      setPersistenceStatus(error instanceof Error ? error.message : "Unable to persist expense.");
    }
  };

  const persistEvidenceValidation = async (evidence: EvidenceItem) => {
    validateEvidence(matter.matterId, evidence.id);
    writeLogs(`${role} User`, "EVIDENCE_VALIDATED", `Validated integrity of: ${evidence.name}`);
    setPersistenceStatus("Updating evidence state...");
    try {
      const result = await updateCaseEvidenceStateCallable({
        clientId: matter.clientId,
        matterId: matter.matterId,
        documentId: evidence.id,
        evidenceState: "VALIDATED",
        note: "Validated from Phase 4 case workspace.",
      });
      setPersistenceStatus(`Evidence state persisted: ${result.data.evidenceState}`);
    } catch (error) {
      setPersistenceStatus(error instanceof Error ? error.message : "Evidence validation persisted locally only.");
    }
  };

  const closeCase = async (closureStatus: "SETTLED" | "WITHDRAWN" | "CLOSED") => {
    setPersistenceStatus("Closing case...");
    try {
      const result = await closeCaseMatterCallable({
        clientId: matter.clientId,
        matterId: matter.matterId,
        closureStatus,
        note: "Closure completed from Phase 4 case workspace.",
      });
      setPersistenceStatus(`Case ${result.data.closureStatus.toLowerCase()}; KC ${result.data.knowledgeCandidateId}`);
    } catch (error) {
      setPersistenceStatus(error instanceof Error ? error.message : "Unable to close case.");
    }
  };

  const createAppeal = async () => {
    setPersistenceStatus("Creating appeal matter...");
    try {
      const result = await createCaseAppealMatterCallable({
        clientId: matter.clientId,
        parentMatterId: matter.matterId,
        title: `${matter.title} - Appeal`,
      });
      setActiveMatterId(result.data.matterId);
      setCurrentRoute(`/cases/${result.data.matterId}`);
      setPersistenceStatus(`Appeal matter created: ${result.data.matterId}`);
    } catch (error) {
      setPersistenceStatus(error instanceof Error ? error.message : "Unable to create appeal matter.");
    }
  };

  // Filter evidence
  const filteredEvidence = matter.evidence.filter(ev => {
    const matchesSearch = ev.name.toLowerCase().includes(evidenceSearch.toLowerCase()) || 
                          ev.excerpt.toLowerCase().includes(evidenceSearch.toLowerCase()) ||
                          ev.entities.some(e => e.toLowerCase().includes(evidenceSearch.toLowerCase()));
    const matchesFilter = evidenceFilter === "ALL" || ev.status === evidenceFilter || ev.type === evidenceFilter;
    return matchesSearch && matchesFilter;
  });

  // Filter witnesses
  const filteredWitnesses = matter.witnesses.filter(w => {
    const matchesSearch = w.name.toLowerCase().includes(witnessSearch.toLowerCase()) || 
                          w.testimonySummary.toLowerCase().includes(witnessSearch.toLowerCase());
    const matchesFilter = witnessFilter === "ALL" || w.side === witnessFilter || w.role === witnessFilter;
    return matchesSearch && matchesFilter;
  });

  // Quick statistics
  const verifiedCount = matter.evidence.filter(e => e.status === "VALIDATED").length;
  const highRiskWitnessCount = matter.witnesses.filter(w => w.credibility === "LOW").length;

  return (
    <div className={`p-4 space-y-4 flex-1 overflow-y-auto bg-slate-55/10 ${isAr ? "text-right" : ""}`} dir={isAr ? "rtl" : "ltr"}>
      <MatterShellBanner species="CASE" />
      
      {/* 1. Case Status Ribbon */}
      <div className="bg-slate-900 text-white rounded-xl py-1 px-4 relative overflow-hidden shadow-md flex items-center justify-between min-h-12">
        {/* Subtle background graphic design element */}
        <div className="absolute right-0 top-0 bottom-0 w-1/3 bg-gradient-to-l from-slate-800/20 to-transparent pointer-events-none"></div>
        <div className="absolute top-1/2 left-1/3 -translate-y-1/2 w-48 h-48 bg-slate-800/10 rounded-full blur-2xl pointer-events-none"></div>
        
        <div className="relative z-10 flex w-full flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <Scale className="text-amber-400 shrink-0" size={16} />
            <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1 min-w-0">
              <h1 className="text-xs md:text-sm font-display font-extrabold text-white tracking-tight leading-none truncate">
                {matter.title}
              </h1>
              <span className="bg-slate-800 text-slate-300 font-bold px-1.5 py-0.5 rounded text-[9px] font-mono shrink-0 border border-slate-700 leading-none">
                {matter.matterId}
              </span>
              <span className="hidden lg:inline-flex items-center gap-1 bg-amber-500/10 text-amber-400 border border-amber-500/20 px-2 py-0.5 rounded-full font-mono font-bold uppercase text-[8.5px] shrink-0 leading-none">
                {enumLabel(matter.lifecycle, lang)}
              </span>
              <span className="hidden md:inline text-slate-650 text-[10px] leading-none">•</span>
              <span className="hidden md:flex items-center gap-1 text-[10.5px] text-slate-300 truncate leading-none">
                <span className="text-slate-400">{isAr ? "المحامي:" : "Counsel:"}</span>
                <strong className="text-white font-bold">{matter.leadLawyer}</strong>
              </span>
              <span className="hidden md:inline text-slate-650 text-[10px] leading-none">•</span>
              <span className="hidden md:flex items-center gap-1 text-[10.5px] text-slate-300 truncate leading-none">
                <span className="text-slate-400">Client:</span>
                <strong className="text-white font-bold">{matter.clientName}</strong>
              </span>
              <span className="hidden xl:inline text-slate-600 text-[10px]">•</span>
              <span className="hidden xl:flex items-center gap-1 text-[10.5px] text-slate-300 truncate">
                <span className="text-slate-400">Bench:</span>
                <strong className="text-white font-bold">{matter.jurisdiction}</strong>
              </span>
            </div>
          </div>

          {/* Key Actions Block & Quick Health Gauge */}
          <div className="flex flex-wrap items-center gap-2 xl:justify-end shrink-0">
            <button 
              type="button"
              onClick={() => {
                openMatterDraft(matter);
                setCurrentRoute("/drafting");
              }}
              className="w-full sm:w-auto py-1 px-2.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-950 text-[10px] font-bold rounded-lg transition-all duration-200 cursor-pointer flex items-center justify-center gap-1 shadow-md shadow-amber-500/5"
            >
              <Bot size={11} className="text-slate-950" />
              <span>{isAr ? "الصياغة بـ AI" : "AI Drafting"}</span>
            </button>
            
            <button 
              type="button"
              onClick={() => setActiveTab("Intelligence")}
              className="w-full sm:w-auto py-1 px-2.5 bg-slate-800 hover:bg-slate-750 text-white border border-slate-700 hover:border-slate-600 text-[10px] font-bold rounded-lg transition-all duration-200 cursor-pointer flex items-center justify-center gap-1 shadow-sm"
            >
              <BrainCircuit size={11} className="text-amber-400" />
              <span>{isAr ? "تشخيص البينة" : "AI Posture"}</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("Intelligence")}
              className="w-full sm:w-auto py-1 px-2.5 bg-white/10 hover:bg-white/15 text-white border border-white/10 text-[10px] font-bold rounded-lg transition-all duration-200 cursor-pointer flex items-center justify-center gap-1 shadow-sm"
            >
              <Bot size={11} className="text-amber-400" />
              <span>{isAr ? "اسأل عن القضية" : "Ask AI about this case"}</span>
            </button>
          </div>
        </div>
      </div>

      {/* 2. Urgent case-level alerts enqueued */}
      {caseAlerts.length > 0 && (
        <div className="bg-rose-50 border border-rose-100/40 text-rose-900 p-3.5 rounded-xl text-xs leading-relaxed flex items-start gap-3 animate-fade-in shadow-xs">
          <div className="bg-rose-100 p-2 rounded-xl text-rose-600 mt-0.5 shrink-0">
            <ShieldAlert size={16} />
          </div>
          <div className="flex-1 space-y-1">
            <strong className="font-black block text-rose-950 uppercase tracking-wider text-[11px]">{isAr ? "تنبيهات عاجلة لمجلس القضية:" : "URGENT COMPLIANCE ALERTS DETECTED:"}</strong>
            <ul className="list-disc pl-4 mt-1.5 space-y-1 font-mono text-[11px] text-rose-800">
              {caseAlerts.map((ca, idx) => (
                <li key={idx} className="font-semibold">{ca.title}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-100 rounded-xl p-3 flex flex-col lg:flex-row lg:items-center justify-between gap-3 shadow-xs text-xs">
        <div className="flex flex-wrap items-center gap-2">
          {["CG1 Intake", "CG2 Evidence", "CG3 Witness", "CG4 Hearing", "CG5 Closure", "CG6 Appeal"].map((gate) => (
            <span key={gate} className="px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-100 font-mono font-bold text-[9px] uppercase">{gate}</span>
          ))}
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          <button type="button" onClick={() => closeCase("SETTLED")} className="w-full sm:w-auto rounded-lg bg-slate-900 text-white px-3 py-1.5 text-[10px] font-bold">Close as Settled</button>
          <button type="button" onClick={createAppeal} className="w-full sm:w-auto rounded-lg bg-white border border-slate-200 text-slate-800 px-3 py-1.5 text-[10px] font-bold">Spawn Appeal Matter</button>
          <button
            type="button"
            onClick={() => {
              if (typeof window !== "undefined" && !window.confirm(
                isAr
                  ? "هل تريد أرشفة هذه القضية؟ سيتم إغلاقها وإخراجها من القوائم النشطة."
                  : "Archive this case? It will be closed and removed from active lists.",
              )) {
                return;
              }
              void closeCase("CLOSED");
            }}
            title={isAr ? "أرشفة هذه القضية" : "Archive this case"}
            className="w-full sm:w-auto rounded-lg bg-white border border-slate-200 text-slate-700 px-3 py-1.5 text-[10px] font-bold hover:border-rose-200 hover:text-rose-700 transition-colors"
          >
            {isAr ? "أرشفة القضية" : "Archive Case"}
          </button>
          {persistenceStatus && <span className="text-[10px] font-semibold text-slate-500">{persistenceStatus}</span>}
        </div>
      </div>

      {/* 3. Redesigned Premium Tabs Bar */}
      <div className="bg-white border border-slate-100 p-1 rounded-xl flex items-center justify-between shadow-xs overflow-x-auto gap-4 scrollbar-none z-10">
        <div className="flex gap-1">
          {([
            { k: "Overview", l: isAr ? "ملخص القضية" : "Case Overview", icon: Scale },
            { k: "Chronicle", l: isAr ? "السجل الزمني" : "Chronicle", icon: History },
            { k: "Evidence", l: isAr ? "خزنة البينات" : "Evidence Vault", icon: Shield },
            { k: "Hearings", l: isAr ? "الجلسات" : "Hearings", icon: Calendar },
            { k: "Expenses", l: isAr ? "المصاريف" : "Expenses", icon: ClipboardList },
            { k: "Witnesses", l: isAr ? "مصفوفة الشهود" : "Witness Matrix", icon: Users },
            { k: "Documents", l: isAr ? "الملف القانوني الموحد" : "Legal Folders", icon: Layers },
            { k: "Intelligence", l: isAr ? "ذكاء الأدلة والتحليل" : "Evidence Intelligence", icon: BrainCircuit }
          ] as const).map(tab => {
            const TabIcon = tab.icon;
            const isTabActive = activeTab === tab.k;
            return (
              <button
                key={tab.k}
                data-testid={tab.k === "Chronicle" ? "case-chronicle-tab" : undefined}
                onClick={() => setActiveTab(tab.k)}
                className={`py-2 px-4.5 text-xs font-bold cursor-pointer transition-all duration-200 shrink-0 select-none flex items-center gap-2 rounded-xl ${
                  isTabActive 
                    ? "bg-slate-900 text-white shadow-md font-extrabold" 
                    : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                }`}
              >
                <TabIcon size={14} className={isTabActive ? "text-amber-400" : "text-slate-400"} />
                <span>{tab.l}</span>
              </button>
            );
          })}
        </div>

        {/* Quick summary status tags on right side of tab-bar */}
        <div className="hidden xl:flex items-center gap-3 text-[10.5px] text-slate-500 font-medium px-4">
          <span className="flex items-center gap-1">
            <span className="h-1.5 w-1.5 bg-emerald-500 rounded-full"></span>
            <strong>{verifiedCount}/{matter.evidence.length}</strong> {isAr ? "معتمد" : "Validated"}
          </span>
          <span className="text-slate-300">|</span>
          <span className="flex items-center gap-1">
            <span className="h-1.5 w-1.5 bg-amber-500 rounded-full"></span>
            <strong>{matter.witnesses.length}</strong> {isAr ? "شهود" : "Witnesses"}
          </span>
        </div>
      </div>

      {/* 4. Tab Context Routing Zone */}
      
      {/* Tab 1: Case Overview */}
      {activeTab === "Overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 animate-fade-in pb-10">
          
          {/* Main Case Facts Summary Column (8/12 widths) */}
          <div className="lg:col-span-8 space-y-6">
            
            {/* Core Matter Facts Box */}
            <div className="bg-white rounded-2xl p-5 shadow-sm space-y-5 border border-slate-100/50">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <h3 className="font-display font-extrabold text-slate-900 text-xs uppercase tracking-widest flex items-center gap-2">
                  <BookMarked size={16} className="text-orange-600" />
                  <span>{isAr ? "صحيفة تفاصيل ملف الدعوى" : "LITIGATION CASE DOSSIER & PARTICULARS"}</span>
                </h3>
                <span className="text-[9.5px] font-mono font-bold text-slate-400 uppercase tracking-widest bg-slate-100 px-2 py-0.5 rounded-md">CONFIDENTIAL ACCESS</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                
                {/* Jurisdiction details */}
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100/40 flex flex-col justify-between space-y-1.5 shadow-2xs">
                  <span className="text-[9.5px] font-mono font-bold text-orange-600 uppercase tracking-wider">{isAr ? "المحكمة والهيئة" : "Forum & Judicial Body:"}</span>
                  <div className="space-y-0.5">
                    <span className="font-black text-slate-900 block text-sm">DIFC Courts - First Instance Chamber</span>
                    <span className="text-[10px] text-slate-400 font-mono">Registry Case Reference: REF-2026-EFI/0042</span>
                  </div>
                </div>

                {/* Judge Details */}
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100/40 flex flex-col justify-between space-y-1.5 shadow-2xs">
                  <span className="text-[9.5px] font-mono font-bold text-orange-600 uppercase tracking-wider">{isAr ? "القاضي المنظر" : "Presiding Judicial Bench:"}</span>
                  <div className="space-y-0.5">
                    <span className="font-black text-slate-900 block text-sm">Justice Omar Al-Mheiri</span>
                    <span className="text-[10px] text-slate-400 font-mono">DIFC Chief Justice panel</span>
                  </div>
                </div>

                {/* Damages Claimed */}
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100/40 flex flex-col justify-between space-y-1.5 shadow-2xs">
                  <span className="text-[9.5px] font-mono font-bold text-rose-600 uppercase tracking-wider">{isAr ? "المطالبات المالية" : "Quantified Liquid Damages:"}</span>
                  <div className="space-y-0.5">
                    <span className="font-black text-rose-600 block text-base">{matter.damagesClaimed || "AED 3,600,050"}</span>
                    <span className="text-[10px] text-slate-400 font-semibold">{isAr ? "إضافة للفائدة النظامية 12%" : "Plus accrued statutory late-payment interest fees"}</span>
                  </div>
                </div>

                {/* Opposing Parties */}
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100/40 flex flex-col justify-between space-y-1.5 shadow-2xs">
                  <span className="text-[9.5px] font-mono font-bold text-orange-600 uppercase tracking-wider">{isAr ? "أطراف الادعاء المقابل" : "Opposing Party & Counsel:"}</span>
                  <div className="space-y-0.5">
                    <span className="font-black text-slate-900 block text-sm">{matter.counterpartyName || "Gulf General Construction Co."}</span>
                    <span className="text-[10px] text-slate-550 font-semibold">Defended by: Al-Suwaidi Advocates SC</span>
                  </div>
                </div>
              </div>

              {/* Legal Basis & Strategic Summary */}
              <div className="p-4 bg-amber-500/[0.03] rounded-xl border border-amber-500/10 space-y-2">
                <span className="text-[9.5px] font-mono font-bold text-amber-700 uppercase tracking-wider block">LAWS & COMPLIANCE POSTURE DETECTOR</span>
                <p className="text-xs text-slate-700 leading-relaxed font-medium">
                  The claimant Rayzen FZE alleges dynamic material breach under UAE Civil Code Article 246 (good faith execution) and Article 390 (contractual liquidated damages assessments). Defense argument pleads sovereign logistics bottlenecks ("force majeure") which our dynamic audit matrix disproves based on corporate inventory invoices.
                </p>
              </div>
            </div>

            {/* Next Court Session / Hearing Redesign */}
            <div className="bg-slate-900 text-white rounded-2xl p-4.5 md:p-5 flex flex-col md:flex-row md:items-center justify-between gap-5 shadow-md relative overflow-hidden">
              <div className="absolute right-0 bottom-0 top-0 w-40 bg-gradient-to-l from-amber-500/5 to-transparent pointer-events-none"></div>
              
              <div className="flex gap-4 items-start relative z-10">
                <div className="p-3 bg-amber-500 text-slate-950 font-mono font-bold rounded-xl shrink-0 flex flex-col items-center justify-center min-w-[70px] shadow-sm">
                  <span className="text-[10px] uppercase font-extrabold">MAY</span>
                  <span className="text-3xl font-black leading-none mt-1">28</span>
                </div>
                <div className="space-y-1.5">
                  <span className="text-[9.5px] font-mono font-bold text-amber-400 block uppercase tracking-wider">
                    {isAr ? "الجلسة القضائية المقبلة" : "CRITICAL UPCOMING COURT SESSION STATUS"}
                  </span>
                  <h4 className="text-sm font-black text-white leading-snug">Oral Pleadings & Testimony Audit</h4>
                  <p className="text-xs text-slate-450 font-mono flex items-center gap-2">
                    <Clock size={13} className="text-amber-400" />
                    <span>09:30 AM • DIFC Court primary chambers room 12 & online</span>
                  </p>
                </div>
              </div>
              
              <button 
                type="button"
                onClick={() => setActiveTab("Intelligence")}
                className="py-2 px-4.5 bg-white hover:bg-slate-50 text-slate-950 text-xs font-bold rounded-xl transition-all duration-200 shadow-sm shrink-0 self-start md:self-auto text-center font-sans cursor-pointer"
              >
                {isAr ? "تحضير تقرير الجلسة" : "Generate Pre-Hearing Brief"}
              </button>
            </div>
          </div>

          {/* Right Sidebar Columns (4/12 widths) */}
          <div className="lg:col-span-4 space-y-6">
            
            {/* Statute of Limitations Countdown Clocks */}
            <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm space-y-4">
              <div className="flex justify-between items-center text-xs">
                <span className="font-extrabold text-slate-900 flex items-center gap-2 uppercase font-mono tracking-wider text-[10px]">
                  <Flame size={14} className="text-rose-600 animate-pulse animate-duration-1000" />
                  Statute Expiry Limit
                </span>
                <span className="font-mono text-rose-650 font-bold bg-rose-50 border border-rose-100/30 px-2.5 py-1 rounded-lg text-xs">
                  {matter.solClockDays} Days Left
                </span>
              </div>
              
              <div className="space-y-1">
                <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
                  <div className="bg-gradient-to-r from-amber-500 to-rose-600 h-2.5 rounded-full shadow-xs" style={{ width: "38%" }}></div>
                </div>
                <div className="flex justify-between text-[10px] text-slate-400 font-mono font-semibold">
                  <span>DOCKET FILED</span>
                  <span>LIMIT EXPIRED</span>
                </div>
              </div>
              <p className="text-[10.5px] text-slate-500 leading-relaxed font-medium">
                Absolute pre-filing claim expansion window. Any secondary cross-petitions must be lodged on the court index before this clock ticks to zero.
              </p>
            </div>

            {/* Quick Witness Matrix Card */}
            <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm space-y-4">
              <div className="flex justify-between items-center pb-2.5 border-b border-slate-100">
                <h3 className="font-display font-extrabold text-slate-900 text-[10px] uppercase tracking-widest flex items-center gap-2">
                  <Users size={14} className="text-slate-650" />
                  <span>{isAr ? "موجز لوحة الشهود" : "Witness Matrix Ledger"}</span>
                </h3>
                <span className="text-[9.5px] font-mono bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full font-bold">
                  {matter.witnesses.length} Active
                </span>
              </div>
              <div className="divide-y divide-slate-100">
                {matter.witnesses.map(w => (
                  <div key={w.witnessId} className="py-2.5 flex items-center justify-between gap-3 text-xs">
                    <div className="space-y-0.5 min-w-0">
                      <span className="font-bold text-slate-900 truncate block">{w.name}</span>
                      <span className="text-[9px] font-mono text-slate-400 font-bold uppercase tracking-wider">{w.role} WITNESS ({w.side})</span>
                    </div>
                    <span className={`px-2 py-0.5 rounded-md text-[8.5px] font-mono tracking-wider font-extrabold border shrink-0 ${
                      w.credibility === "HIGH" 
                        ? "bg-emerald-50 text-emerald-700 border-emerald-100/60" 
                        : "bg-amber-50 text-amber-700 border-amber-100/60"
                    }`}>
                      {w.credibility} RISK
                    </span>
                  </div>
                ))}
              </div>
              <button 
                type="button"
                onClick={() => setActiveTab("Witnesses")}
                className="w-full text-center py-2 bg-slate-50 hover:bg-slate-100 rounded-xl text-[10.5px] font-bold text-slate-700 border border-slate-100 hover:border-slate-200 transition-colors cursor-pointer"
              >
                {isAr ? "عرض المصفوفة الكاملة" : "Manage Testimony Playbooks"}
              </button>
            </div>

            {/* Audit Team Verification Card */}
            <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm space-y-3.5">
              <h3 className="font-display font-black text-slate-900 text-[10px] uppercase tracking-widest flex items-center gap-1.5">
                <CheckCircle2 size={13} className="text-emerald-600" />
                <span>Caseload Auditing Team</span>
              </h3>
              <div className="flex items-center gap-3">
                <div className="flex -space-x-2 overflow-hidden">
                  <span className="h-8.5 w-8.5 rounded-xl bg-slate-900 text-slate-100 text-[11px] font-mono font-bold flex items-center justify-center border-2 border-white shadow-xs">SA</span>
                  <span className="h-8.5 w-8.5 rounded-xl bg-amber-500 text-slate-950 text-[11px] font-mono font-bold flex items-center justify-center border-2 border-white shadow-xs">MK</span>
                  <span className="h-8.5 w-8.5 rounded-xl bg-orange-600 text-white text-[11px] font-mono font-bold flex items-center justify-center border-2 border-white shadow-xs">AI</span>
                </div>
                <div className="text-[10.5px] font-medium text-slate-505 leading-snug">
                  <span>Liaison Counsel: <strong>Sabique Ahmed</strong> & <strong>Mary Kelly</strong>. Autopilot verification active.</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      )}

      {activeTab === "Chronicle" && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 animate-fade-in pb-10 text-xs">
          <section className="lg:col-span-8 bg-white border border-slate-100 rounded-xl p-5 shadow-xs space-y-4">
            <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-3">
              <h3 className="text-xs font-black uppercase tracking-widest text-slate-900 flex items-center gap-2"><History size={15} className="text-orange-600" /> Matter Chronicle</h3>
              <span className="text-[10px] font-mono font-bold text-slate-400">TIMELINE WRITEBACK</span>
            </div>
            <p className="text-[11px] text-slate-500 leading-relaxed">Record a matter-scoped timeline event, negotiation update, or court activity directly against this case workspace.</p>
            <textarea
              value={chronicleEntry}
              onChange={(event) => setChronicleEntry(event.target.value)}
              placeholder="Enter a case chronology note, filing activity, or strategic update..."
              className="min-h-[180px] w-full rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-800 outline-none focus:ring-1 focus:ring-slate-900"
            />
            <div className="flex items-center justify-between gap-3">
              <span className="text-[10px] font-semibold text-slate-500">Saved entries remain matter-scoped to {matter.matterId}.</span>
              <button type="button" onClick={saveChronicleEntry} className="rounded-xl bg-slate-900 px-4 py-2 text-xs font-bold text-white">Save Timeline Entry</button>
            </div>
            {chronicleStatus && <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-2 text-[11px] font-semibold text-emerald-700">{chronicleStatus}</div>}
          </section>
          <aside className="lg:col-span-4 bg-white border border-slate-100 rounded-xl p-5 shadow-xs space-y-3">
            <h3 className="text-xs font-black uppercase text-slate-900">Chronology Anchors</h3>
            <div className="space-y-2 text-[11px] text-slate-600">
              <div className="rounded-xl border border-slate-100 bg-slate-50 p-3"><strong className="block text-slate-900">Pleadings filed</strong> Statement of claim and defense are indexed in Legal Folders.</div>
              <div className="rounded-xl border border-slate-100 bg-slate-50 p-3"><strong className="block text-slate-900">Next hearing</strong> Directions hearing is already mirrored in the Hearings tab.</div>
              <div className="rounded-xl border border-slate-100 bg-slate-50 p-3"><strong className="block text-slate-900">Evidence posture</strong> Vault validation status updates can be referenced in saved chronology notes.</div>
            </div>
          </aside>
        </div>
      )}

      {/* Tab 2: Evidence Vault */}
      {activeTab === "Evidence" && (
        <div className="space-y-6 animate-fade-in pb-10">
          
          {/* Header & Vault Controller Bar */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-4.5 rounded-xl border border-slate-100 shadow-sm">
            <div className="space-y-1">
              <h3 className="text-xs font-display font-extrabold text-slate-900 uppercase tracking-widest flex items-center gap-2">
                <Shield size={16} className="text-orange-600" />
                <span>{isAr ? "خزنة الإثباتات والتحاليل الرقمية المعقدة" : "Evidence Asset Locker & Digital Chain-of-Custody"}</span>
              </h3>
              <p className="text-[11.5px] text-slate-500">{isAr ? "جميع وثائق الإثبات تم تدقيقها وصح عزوها وفحص الـ Hash لها بفاعلية" : "Every electronic exhibit or legal disclosure holds a unique cryptographic sealer and admissibility clearance rating."}</p>
            </div>
            
            <button 
              type="button"
              onClick={() => setShowUploadModal(true)}
              className="py-2 px-4 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center gap-1.5 shadow-md shrink-0 justify-center"
            >
              <Upload size={13} />
              <span>{isAr ? "تصدير وثيقة إثبات" : "Add Secure Exhibit"}</span>
            </button>
          </div>

          {/* Filtering and Search Controls */}
          <div className="bg-slate-50 border border-slate-100/55 p-3.5 rounded-xl flex flex-col md:flex-row gap-4 items-center">
            {/* Search Input */}
            <div className="relative w-full md:w-72">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-455" size={14} />
              <input 
                type="text"
                placeholder={isAr ? "البحث عن مستند او كيان..." : "Search exhibits or tag entities..."}
                value={evidenceSearch}
                onChange={(e) => setEvidenceSearch(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-xl pl-9 pr-3 py-1.8 text-xs outline-none focus:ring-1 focus:ring-slate-900"
              />
            </div>

            {/* Selection tags filter */}
            <div className="flex flex-wrap gap-1.5 items-center w-full md:w-auto">
              <span className="text-[10px] uppercase font-mono text-slate-400 font-bold mr-2 flex items-center gap-1">
                <Filter size={10} /> Filter:
              </span>
              {(["ALL", "Expert Report", "Invoice", "Email", "VALIDATED", "NEW"] as const).map(tag => (
                <button
                  key={tag}
                  onClick={() => setEvidenceFilter(tag)}
                  className={`px-3 py-1 rounded-lg text-[10.5px] font-bold border transition-colors cursor-pointer ${
                    evidenceFilter === tag 
                      ? "bg-slate-900 text-white border-transparent shadow-3xs" 
                      : "bg-white hover:bg-slate-100 text-slate-600 border-slate-200"
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Evidence Grid Redesigned (Premium Modular Cards) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredEvidence.length > 0 ? (
              filteredEvidence.map(ev => {
                const isCompromised = ev.status === "INTEGRITY_COMPROMISED";
                const isValidated = ev.status === "VALIDATED";
                return (
                  <div 
                    key={ev.id} 
                    className={`bg-white p-5 rounded-xl transition-all duration-205 flex flex-col justify-between hover:scale-[1.002] shadow-sm hover:shadow-md border ${
                      isValidated 
                        ? "border-emerald-500/20 bg-emerald-500/[0.01]" 
                        : isCompromised
                        ? "border-rose-500/20 bg-rose-500/[0.01]"
                        : "border-slate-100"
                    }`}
                  >
                    
                    {/* Header sector of document card */}
                    <div className="space-y-4">
                      
                      <div className="flex items-center justify-between">
                        <span className="text-[9.5px] font-mono bg-slate-100 text-slate-700 font-bold px-2.5 py-0.5 rounded-md border border-slate-200/50 uppercase tracking-widest">
                          {enumLabel(ev.type, lang)}
                        </span>
                        
                        <span className={`text-[9.5px] font-mono font-bold uppercase px-2.5 py-0.8 rounded-md border flex items-center gap-1 tracking-wider ${
                          isValidated 
                            ? "bg-emerald-50 text-emerald-800 border-emerald-100/60" 
                            : isCompromised 
                            ? "bg-rose-50 text-rose-800 border-rose-100/60 animate-pulse" 
                            : "bg-amber-50 text-amber-800 border-amber-100/60"
                        }`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${isValidated ? "bg-emerald-500" : isCompromised ? "bg-rose-500" : "bg-amber-500 animate-pulse"}`}></span>
                          {enumLabel(ev.status, lang)}
                        </span>
                      </div>

                      <div className="space-y-1">
                        <h4 className="text-sm font-black text-slate-900 flex items-center gap-2">
                          <FileSignature className="text-slate-400 shrink-0" size={16} />
                          <span>{ev.name}</span>
                        </h4>
                        <div className="text-[10px] text-slate-400 font-mono">
                          <span>Uploaded: {ev.uploadedAt} • Size: {ev.size}</span>
                        </div>
                      </div>

                      {/* Excerpt Container */}
                      <div className="bg-slate-50 border border-slate-100/50 p-4 rounded-xl relative">
                        <p className="text-xs text-slate-755 italic leading-relaxed pl-3 border-l-2 border-slate-300 font-medium">
                          "{ev.excerpt}"
                        </p>
                      </div>
                    </div>

                    {/* Metadata tags, Cryptographic hashes and Actions */}
                    <div className="pt-4 border-t border-slate-100 flex flex-col gap-3 mt-4 text-[11px] font-sans">
                      
                      {/* Detected Entities */}
                      <div className="space-y-1">
                        <span className="text-[9px] font-mono font-bold uppercase tracking-wider text-slate-400 block">AI DETECTED ENTITIES:</span>
                        <div className="flex flex-wrap gap-1">
                          {ev.entities.map(ent => (
                            <span key={ent} className="bg-slate-50 text-slate-700 px-2 py-0.5 rounded-md font-bold text-[9px] border border-slate-100">
                              {ent}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Cryptographic hash mock layout */}
                      <div className="flex items-center gap-1.5 text-[10.5px] font-mono text-slate-500 bg-slate-50/50 p-1.5 rounded-md w-full">
                        <Fingerprint size={12} className="text-slate-400" />
                        <span className="text-[9px] truncate">SHA256: e8f4e2c7a9b0c1e8ac9d4e3f22da9e9f88d22c954a938c11f</span>
                      </div>

                      {/* Compliance Warnings if any */}
                      {ev.admissibilityFlags.length > 0 && (
                        <div className="p-2.5 bg-rose-50 border border-rose-100/40 text-rose-800 rounded-xl text-[10.5px] font-mono flex items-start gap-1.5 font-bold">
                          <AlertTriangle size={14} className="text-rose-600 mt-0.5 shrink-0" />
                          <div className="space-y-0.5">
                            <span className="uppercase block text-[9.5px]">ADMISSIBILITY COMPLIANCE WARNING</span>
                            <span className="font-semibold text-rose-700 block text-[9.5px]">{ev.admissibilityFlags[0]}</span>
                          </div>
                        </div>
                      )}

                      {/* Action buttons with interactive state */}
                      <div className="flex items-center justify-between pt-3 border-t border-slate-100 mt-1 leading-none">
                        <span className="text-[10px] text-slate-400 font-mono font-semibold">Authorized: System Clerk</span>
                        
                        {!isValidated ? (
                          <button 
                            type="button"
                            onClick={() => persistEvidenceValidation(ev)}
                            className="py-1.5 px-3 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-[10px] font-bold transition-all cursor-pointer shadow-xs select-none uppercase tracking-wider"
                          >
                            {isAr ? "موافقة على القبول" : "Verify Integrity & Validate"}
                          </button>
                        ) : (
                          <div className="text-emerald-700 font-extrabold flex items-center gap-1 text-[11px] font-mono">
                            <CheckCircle2 size={13} className="text-emerald-600 animate-pulse" /> 
                            <span>COMPLIANT REGISTER</span>
                          </div>
                        )}
                      </div>

                    </div>
                  </div>
                );
              })
            ) : (
              <div className="bg-white rounded-xl border border-slate-100 p-12 text-center col-span-2 space-y-3 shadow-sm">
                <Shield size={32} className="text-slate-300 mx-auto" />
                <p className="text-slate-500 font-mono text-xs font-bold uppercase">{isAr ? "لم يتم العثور على وثائق إثبات" : "No evidentiary discoveries found matching query criteria."}</p>
                <button
                  onClick={() => { setEvidenceSearch(""); setEvidenceFilter("ALL"); }}
                  className="px-4 py-1.5 bg-slate-900 text-white text-xs font-bold rounded-xl cursor-pointer"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>

        </div>
      )}

      {/* Tab 3: Witness Matrix */}
      {activeTab === "Hearings" && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 animate-fade-in text-xs pb-10">
          <section className="lg:col-span-8 bg-white border border-slate-100 rounded-xl p-5 shadow-xs space-y-4">
            <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-3">
              <h3 className="text-xs font-black uppercase tracking-widest text-slate-900 flex items-center gap-2"><Calendar size={15} className="text-orange-600" /> Hearing Calendar</h3>
              <span className="text-[10px] font-mono font-bold text-slate-400">CG4 COURT READINESS</span>
            </div>
            {[
              { title: "Directions hearing", date: "2026-05-28", brief: "Hearing brief due 24h before session." },
              { title: "Expert evidence conference", date: "2026-06-09", brief: "Bundle exhibits and expert chronology required." },
            ].map((hearing) => (
              <div key={hearing.title} className="rounded-xl border border-slate-100 bg-slate-50/70 p-4 flex items-center justify-between gap-3">
                <div>
                  <strong className="text-slate-950 block">{hearing.title}</strong>
                  <span className="text-[10.5px] text-slate-500">{hearing.brief}</span>
                </div>
                <span className="font-mono font-bold text-orange-700 bg-orange-50 border border-orange-100 rounded-lg px-2 py-1">{hearing.date}</span>
              </div>
            ))}
          </section>
          <aside className="lg:col-span-4 bg-white border border-slate-100 rounded-xl p-5 shadow-xs space-y-3">
            <h3 className="text-xs font-black uppercase text-slate-900">Auto-deadline Writeback</h3>
            <p className="text-[11px] text-slate-500 leading-relaxed">Phase 4 hook point for hearing CRUD, court deadline projection, and Generate Hearing Brief actions.</p>
            <button type="button" onClick={persistHearing} className="w-full rounded-xl bg-slate-900 text-white py-2 text-xs font-bold">Persist Hearing</button>
            <button
              type="button"
              onClick={() => {
                writeLogs(`${role} User`, "HEARING_BRIEF_GENERATED", `Pre-hearing brief drafted for ${matter.matterId}.`);
                setPersistenceStatus(isAr ? "تم إنشاء مسودة موجز الجلسة." : "Hearing brief draft generated.");
              }}
              className="w-full rounded-xl bg-white border border-slate-200 text-slate-800 py-2 text-xs font-bold"
            >
              Generate Hearing Brief
            </button>
            {persistenceStatus && <p className="text-[10px] font-semibold text-slate-500">{persistenceStatus}</p>}
          </aside>
        </div>
      )}

      {activeTab === "Expenses" && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 animate-fade-in p-5 space-y-4 pb-10 text-xs">
          <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-3">
            <h3 className="text-xs font-black uppercase tracking-widest text-slate-900 flex items-center gap-2"><ClipboardList size={15} className="text-orange-600" /> Case Expenses</h3>
            <span className="text-[10px] font-mono font-bold text-slate-400">MATTER-SCOPED WIP</span>
          </div>
          {[
            { label: "Court filing fees", amount: "AED 4,200", status: "Recoverable" },
            { label: "Expert conference retainer", amount: "AED 18,000", status: "Client approval pending" },
            { label: "Courier and notarisation", amount: "AED 650", status: "Posted to WIP" },
          ].map((expense) => (
            <div key={expense.label} className="grid grid-cols-3 gap-3 rounded-xl border border-slate-100 bg-slate-50/70 p-3">
              <strong className="text-slate-900">{expense.label}</strong>
              <span className="font-mono font-bold text-slate-700">{expense.amount}</span>
              <span className="text-slate-500">{expense.status}</span>
            </div>
          ))}
          <button type="button" onClick={persistExpense} className="rounded-xl bg-slate-900 text-white px-4 py-2 text-xs font-bold">Post First Expense to Nested Matter</button>
          {persistenceStatus && <p className="text-[10px] font-semibold text-slate-500">{persistenceStatus}</p>}
        </div>
      )}

      {activeTab === "Witnesses" && (
        <div className="space-y-6 animate-fade-in pb-10">
          
          {/* Header matrix panel */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-4.5 rounded-xl border border-slate-100 shadow-sm font-sans">
            <div>
              <h3 className="text-xs font-display font-extrabold text-slate-900 uppercase tracking-widest flex items-center gap-2">
                <Users size={16} className="text-orange-600" />
                <span>{isAr ? "مجلس فحص شهود الخصومة" : "Witness Testimony Registry & Deposition Matrix"}</span>
              </h3>
              <p className="text-[11.5px] text-slate-500 mt-1">{isAr ? "جدولة بيانات الشهود وتقييم دقة الشهادة وصياغة محاور الاستجواب" : "Map and cross-audit depositor statements against invoices to reveal material discrepancies."}</p>
            </div>
            
            <button 
              type="button"
              onClick={() => setShowWitnessDrawer(true)}
              className="py-2 px-4 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center gap-1.5 shadow-md shrink-0 justify-center"
            >
              <PlusCircle size={13} />
              <span>{isAr ? "تسجيل شاهد جديد" : "Onboard Witness Depositor"}</span>
            </button>
          </div>

          {/* Local Search and Filtering widgets */}
          <div className="bg-slate-50 border border-slate-100/55 p-3.5 rounded-xl flex flex-col md:flex-row gap-4 items-center">
            {/* Search Input */}
            <div className="relative w-full md:w-72">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-450" size={14} />
              <input 
                type="text"
                placeholder={isAr ? "ابحث بالاسم او جزء من الشهادة..." : "Search witness name or testimony keywords..."}
                value={witnessSearch}
                onChange={(e) => setWitnessSearch(e.target.value)}
                className="w-full bg-white border border-slate-205 rounded-xl pl-9 pr-3 py-1.8 text-xs outline-none focus:ring-1 focus:ring-slate-900"
              />
            </div>

            {/* Side Filters */}
            <div className="flex flex-wrap gap-1.5 items-center w-full md:w-auto">
              <span className="text-[10px] uppercase font-mono text-slate-400 font-bold mr-2 flex items-center gap-1">
                <Filter size={10} /> Party Side:
              </span>
              {(["ALL", "PLAINTIFF", "DEFENDANT", "FACT", "EXPERT"] as const).map(tag => (
                <button
                  key={tag}
                  onClick={() => setWitnessFilter(tag)}
                  className={`px-3 py-1 rounded-lg text-[10.5px] font-bold border transition-colors cursor-pointer ${
                    witnessFilter === tag 
                      ? "bg-slate-900 text-white border-transparent shadow-3xs" 
                      : "bg-white hover:bg-slate-100 text-slate-600 border-slate-200"
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Matrix Ledger Details (Split cards with profiles & AI crossplaybooks) */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {filteredWitnesses.length > 0 ? (
              filteredWitnesses.map(w => {
                const credibilityColor = 
                  w.credibility === "HIGH" 
                    ? "bg-emerald-50 text-emerald-800 border-emerald-100/60" 
                    : w.credibility === "MEDIUM"
                    ? "bg-amber-50 text-amber-800 border-amber-100/60"
                    : "bg-rose-50 text-rose-800 border-rose-100/60 animate-pulse";
                
                return (
                  <div 
                    key={w.witnessId} 
                    className="bg-white p-5 shadow-sm rounded-xl border border-slate-100/60 hover:border-slate-200 flex flex-col justify-between space-y-4 transition-all duration-200 hover:shadow-md"
                  >
                    <div className="space-y-4">
                      {/* Top sectors */}
                      <div className="flex justify-between items-start gap-3 border-b border-slate-105/50 pb-3">
                        <div className="space-y-1">
                          <h4 className="text-sm font-black text-slate-900 flex items-center gap-2">
                            <User size={15} className="text-slate-600" />
                            <span>{w.name} Direct Deposition</span>
                          </h4>
                          <div className="flex flex-wrap items-center gap-2 text-[9.5px] font-mono font-bold text-slate-400 uppercase tracking-wider">
                            <span>CLASSIFICATION: {w.role}</span>
                            <span>•</span>
                            <span>AFFILIATION: <strong className={w.side === "PLAINTIFF" ? "text-emerald-700" : "text-amber-700"}>{w.side}</strong></span>
                          </div>
                        </div>

                        <span className={`px-2.5 py-1 rounded-md text-[9.5px] font-mono font-bold border uppercase tracking-widest ${credibilityColor}`}>
                          {w.credibility} CREDIBILITY
                        </span>
                      </div>

                      {/* Brief Testimony bubble */}
                      <div className="bg-slate-50 border border-slate-100/50 p-4 rounded-xl relative text-xs shadow-2xs">
                        <span className="text-[8.5px] font-mono text-slate-400 block pb-1.5 uppercase font-black">RECORDED DISCLOSURE OVERVIEW:</span>
                        <p className="text-slate-705 italic font-medium leading-relaxed pl-3.5 border-l-2 border-slate-300">
                          "{w.testimonySummary}"
                        </p>
                      </div>
                    </div>

                    {/* AI Deposition Crossexamination Strategy Maps */}
                    <div className="space-y-3 pt-4 border-t border-slate-100 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] uppercase font-mono font-black text-slate-900 flex items-center gap-1.5">
                          <BrainCircuit size={14} className="text-amber-500 shrink-0" />
                          <span>AI Cross-Examination Playbook Paths</span>
                        </span>
                        <span className="text-[9.5px] font-mono text-slate-420 bg-slate-100 px-2 py-0.5 rounded-md font-bold">RELIABILITY CHECKS</span>
                      </div>
                      
                      <div className="space-y-2">
                        {w.crossExamQuestions.map((q, idx) => (
                          <div key={idx} className="bg-slate-50/50 hover:bg-slate-50 p-3 rounded-xl border border-slate-100/40 flex gap-2.5 text-[11px] leading-relaxed transition-all duration-150 shadow-3xs">
                            <div className="h-5.5 w-5.5 rounded-lg bg-slate-900 text-white text-[10px] font-mono font-bold flex items-center justify-center shrink-0 shadow-xs">
                              Q{idx + 1}
                            </div>
                            <div className="space-y-0.5">
                              <p className="text-slate-755 font-bold leading-relaxed">{q}</p>
                              <span className="text-[8.5px] font-mono text-slate-400 uppercase block">TARGET: IMPEACHMENT & AUDIT RECONCILIATION</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                  </div>
                );
              })
            ) : (
              <div className="bg-white rounded-xl border border-slate-100 p-12 text-center col-span-2 space-y-3 shadow-sm">
                <Users size={32} className="text-slate-300 mx-auto" />
                <p className="text-slate-500 font-mono text-xs font-bold uppercase">{isAr ? "لم يتم العثور على شهود بهذا التصنيف" : "No witness depositions found matching active matrix filters."}</p>
                <button
                  onClick={() => { setWitnessSearch(""); setWitnessFilter("ALL"); }}
                  className="px-4 py-1.5 bg-slate-900 text-white text-xs font-bold rounded-xl cursor-pointer"
                >
                  Reset Active Filters
                </button>
              </div>
            )}
          </div>

        </div>
      )}

      {/* Tab 4: Legal Folders & Virtual Directories */}
      {activeTab === "Documents" && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 animate-fade-in p-5 space-y-6 pb-10 font-sans">
          
          {/* Header row and index pack bundler controls */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5 border-b border-slate-100 pb-5">
            <div className="space-y-1">
              <h3 className="text-xs font-display font-extrabold text-slate-900 uppercase tracking-widest flex items-center gap-2">
                <Layers size={16} className="text-orange-600" />
                <span>Matter Directory & Sealed Legal Exhibit Folders</span>
              </h3>
              <p className="text-[11.5px] text-slate-500">{isAr ? "مجالس المستندات مقسمة وملخصة وموثقة إلكترونياً مع الأرشيف العام" : "Collated legal directory tracking pleadings, filings, and chronological exhibits. Secure encryption tags generated."}</p>
            </div>

            {/* Micro Dynamic Exhibit Compiler */}
            <div className="flex flex-col sm:flex-row gap-2">
              <button 
                type="button"
                onClick={triggerBundleCompilation}
                disabled={compilingBundle}
                className="py-2.5 px-4.5 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-600 disabled:cursor-not-allowed text-white text-xs font-black rounded-xl transition-all duration-150 cursor-pointer flex items-center justify-center gap-1.5 shadow-md"
              >
                <Layers size={13} className={compilingBundle ? "animate-spin" : ""} />
                  <span>{compilingBundle ? (isAr ? "جاري تشكيل الملف وتوقيع الهاش..." : "Building export bundle...") : (isAr ? "تجميع الملف القضائي" : "Compile Sealed Court Bundle")}</span>
              </button>

              {bundleDownloadReady && (
                <button 
                  type="button"
                  onClick={() => {
                    writeLogs(`${role} User`, "CASE_BUNDLE_DOWNLOAD", `Sealed bundle package opened: ${bundleExportId ?? caseBundlePlan.bundleId}.`);
                    setPersistenceStatus(isAr ? "تم فتح حزمة الملف المختوم." : `Sealed bundle ready: ${bundleExportId ?? caseBundlePlan.bundleId}.`);
                  }}
                  className="py-2.5 px-4.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-extrabold flex items-center justify-center gap-1.5 animate-pulse cursor-pointer border-transparent shadow-md"
                >
                  <Download size={13} />
                  <span>Open SEALED Bundle Package</span>
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
              <span className="block text-[9px] font-mono font-black uppercase text-slate-400">Readiness</span>
              <strong className="mt-1 block text-slate-950">{caseBundlePlan.readiness}</strong>
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
              <span className="block text-[9px] font-mono font-black uppercase text-slate-400">Manifest</span>
              <strong className="mt-1 block text-slate-950">{caseBundlePlan.sealedHash}</strong>
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
              <span className="block text-[9px] font-mono font-black uppercase text-slate-400">Documents</span>
              <strong className="mt-1 block text-slate-950">{caseBundlePlan.validatedDocuments}/{caseBundlePlan.totalDocuments} ready</strong>
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
              <span className="block text-[9px] font-mono font-black uppercase text-slate-400">Repository</span>
              <strong className="mt-1 block text-slate-950">{firmDocuments.sourceState}</strong>
            </div>
          </div>

          {caseBundlePlan.blockers.length > 0 && (
            <div className="rounded-xl border border-amber-100 bg-amber-50/60 p-3 text-[11px] font-semibold text-amber-800 space-y-1">
              {caseBundlePlan.blockers.map((blocker) => <p key={blocker}>{blocker}</p>)}
            </div>
          )}

          {/* Virtual File Structure - Grid list of Collapsible group directories */}
          <div className="space-y-5 text-xs text-slate-700">
            {caseBundlePlan.groups.map((group) => {
              const open = foldersOpen[group.key];
              const FolderIcon = group.key === "exhibits" ? FileSpreadsheet : group.key === "internal" ? Lock : FileText;
              return (
                <div key={group.key} className="border border-slate-100 rounded-xl overflow-hidden shadow-2xs bg-white">
                  <button
                    type="button"
                    onClick={() => setFoldersOpen(prev => ({ ...prev, [group.key]: !prev[group.key] }))}
                    className="w-full bg-slate-50/50 px-4 py-3 flex items-center justify-between border-b border-slate-100 cursor-pointer hover:bg-slate-50/80 transition-all font-sans"
                  >
                    <div className="flex items-center gap-2.5">
                      <ChevronDown size={16} className={`text-slate-500 transition-transform duration-200 ${open ? "" : "-rotate-90"}`} />
                      <FolderIcon size={16} className="text-orange-500 shrink-0" />
                      <span className="font-extrabold text-slate-900 text-xs tracking-wider uppercase">{group.label}</span>
                    </div>
                    <span className="text-[10px] font-mono text-slate-455 bg-white border border-slate-100 px-2.5 py-0.5 rounded-md font-bold">{group.documents.length} FILES</span>
                  </button>

                  {open && (
                    <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3 bg-white text-xs">
                      {group.documents.map((document) => (
                        <div key={document.id} className="bg-slate-50/50 hover:bg-slate-50 border border-slate-100/60 p-3.5 rounded-xl flex items-center justify-between transition-all shadow-3xs">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="p-2.5 bg-orange-50 text-orange-600 rounded-lg">
                              <FolderIcon size={16} />
                            </div>
                            <div className="min-w-0">
                              <strong className="font-black text-slate-900 text-xs truncate block">{document.name}</strong>
                              <span className="text-[9.5px] text-slate-400 font-mono truncate block">{document.sourcePath}</span>
                            </div>
                          </div>
                          <span className="text-[10px] font-mono text-slate-455 bg-white border border-slate-100 px-2.5 py-0.5 rounded-md shrink-0 ml-2 font-bold leading-none select-none uppercase">{document.status}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

          </div>
        </div>
      )}

      {/* Tab 5: Evidence Intelligence Summary (AI Cognitive Synthesis) */}
      {activeTab === "Intelligence" && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5 space-y-6 animate-fade-in relative pb-10 font-sans">
          
          {/* Header of analytics cognitive zone */}
          <div className="flex justify-between items-center border-b border-orange-100/50 pb-4.5 flex-wrap gap-3">
            <div className="space-y-1">
              <h3 className="text-xs font-display font-extrabold text-slate-900 uppercase tracking-widest flex items-center gap-2">
                <BrainCircuit size={18} className="text-slate-950 shrink-0" />
                <span>{isAr ? "تحليل البينة المدمجة والمستشار الذكي" : "AI Evidentiary Posture & Litigation Strength Analytics"}</span>
              </h3>
              <p className="text-[11.5px] text-slate-550">
                {isAr ? "أبعاد تدفق معلومات الإثبات واستخراج التناقضات وصحة المسار القضائي" : "Interactive assessment of lawsuit strength, adversarial depositions, and critical timeline gaps."}
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 text-[10px] font-mono font-bold bg-orange-50 text-orange-850 rounded-lg border border-orange-100 flex items-center gap-1.5 select-none">
                <Sparkles size={11} className="text-orange-600 inline-block animate-pulse shrink-0" />
                <span>DIFC Civil Code Ruleset Enforced</span>
              </span>
            </div>
          </div>

          <p className="text-xs text-slate-650 leading-relaxed font-semibold">
            This analytics panel automatically synthesizes all active files in the <strong>Evidence Vault</strong> and <strong>Witness Matrix</strong> to measure lawsuit completeness, cross-check timeline discrepancies, and draft rebuttal clauses.
          </p>

          {/* Interactive Strength Meters and Gauges */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            
            {/* Gauge 1: Probability of Successive Summary Judgment */}
            <div className="bg-slate-50/50 border border-slate-100 p-4.5 rounded-xl space-y-3.5 shadow-3xs">
              <div className="flex justify-between items-center text-xs">
                <span className="font-extrabold text-slate-900 uppercase tracking-wider text-[10px] font-mono">PLAINTIFF POSTURE INDEX</span>
                <span className="font-mono text-emerald-700 font-extrabold text-xs bg-emerald-50 border border-emerald-100/65 px-2 py-0.5 rounded-md">{confidenceScore}%</span>
              </div>
              
              <div className="space-y-1.5">
                <div className="w-full bg-slate-205/60 rounded-full h-3 overflow-hidden flex">
                  <div className="bg-gradient-to-r from-emerald-500 to-teal-650 h-3 rounded-full" style={{ width: `${confidenceScore}%` }}></div>
                </div>
                <div className="flex justify-between text-[10px] text-slate-455 font-mono font-bold">
                  <span>UNSTABLE</span>
                  <span>SOLID PLEAD</span>
                </div>
              </div>
              <p className="text-[10.5px] text-slate-500 leading-normal font-medium">
                Propelled by the authenticated corporate invoice which contradicts Yousuf Khan's claimed extension dates.
              </p>
              <div className="pt-2 flex items-center gap-1.5">
                <button 
                  onClick={() => setConfidenceScore(c => Math.min(100, c + 2))}
                  className="px-2 py-1 bg-white hover:bg-slate-50 border border-slate-100/80 text-[9.5px] font-mono font-bold rounded-md cursor-pointer"
                >
                  Override +2%
                </button>
                <button 
                  onClick={() => setConfidenceScore(82)}
                  className="px-2 py-1 bg-white hover:bg-slate-50 border border-slate-100/80 text-[9.5px] font-mono font-bold rounded-md text-slate-400 cursor-pointer"
                >
                  Reset
                </button>
              </div>
            </div>

            {/* Gauge 2: Fact-Finding Completeness */}
            <div className="bg-slate-50/50 border border-slate-100 p-4.5 rounded-xl space-y-3.5 shadow-3xs">
              <div className="flex justify-between items-center text-xs">
                <span className="font-extrabold text-slate-900 uppercase tracking-wider text-[10px] font-mono">DISCLOSURE PROGRESS</span>
                <span className="font-mono text-orange-700 font-extrabold text-xs bg-orange-50 border border-orange-100/65 px-2 py-0.5 rounded-md">{evidenceMaturity}%</span>
              </div>
              
              <div className="space-y-1.5">
                <div className="w-full bg-slate-205/60 rounded-full h-3 overflow-hidden flex">
                  <div className="bg-gradient-to-r from-orange-500 to-orange-600 h-3 rounded-full" style={{ width: `${evidenceMaturity}%` }}></div>
                </div>
                <div className="flex justify-between text-[10px] text-slate-455 font-mono font-bold">
                  <span>INCOMPLETE</span>
                  <span>FULLY AUDITED</span>
                </div>
              </div>
              <p className="text-[10.5px] text-slate-500 leading-normal font-medium">
                All uploaded items have been structured, parsed on OCR pathways, and cross-referenced with regional registries.
              </p>
              <div className="pt-2 flex items-center gap-1.5">
                <button 
                  onClick={() => setEvidenceMaturity(c => Math.min(100, c + 1.5))}
                  className="px-2 py-1 bg-white hover:bg-slate-50 border border-slate-100/80 text-[9.5px] font-mono font-bold rounded-md cursor-pointer"
                >
                  Add Fact Link
                </button>
                <button 
                  onClick={() => setEvidenceMaturity(89)}
                  className="px-2 py-1 bg-white hover:bg-slate-50 border border-slate-100/80 text-[9.5px] font-mono font-bold rounded-md text-slate-400 cursor-pointer"
                >
                  Reset
                </button>
              </div>
            </div>

            {/* Gauge 3: Open Evidentiary Defenses */}
            <div className="bg-slate-50/50 border border-slate-100 p-4.5 rounded-xl flex flex-col justify-between space-y-3 shadow-3xs">
              <div className="space-y-1.5">
                <span className="font-extrabold text-slate-950 uppercase tracking-wider text-[10px] font-mono block">OUTSTANDING CONTRADICTIONS</span>
                <div className="text-2xl font-black text-slate-900 font-mono">2 Critical Blocks</div>
                <p className="text-[10.5px] text-slate-500 leading-normal font-medium">
                  Two significant discrepancies are registered between direct text witnesses and chronological delivery logs.
                </p>
              </div>
              <span className="text-[9.5px] text-rose-700 font-extrabold bg-rose-50 border border-rose-100/65 rounded-md p-1.5 block leading-none font-mono uppercase text-center">
                Requires Cross Impeachment Clauses
              </span>
            </div>

          </div>

          {/* Forensic Playbook Contradictions Detected */}
          <div className="space-y-4">
            <h4 className="text-xs font-mono font-black text-slate-900 uppercase tracking-widest border-b border-slate-100 pb-2 flex items-center gap-2">
              <BadgeAlert size={14} className="text-rose-600" />
              <span>DETECTED ADVERSARIAL CONFLICT REPORTS</span>
            </h4>

            {/* Finding Item 1 with interactive hover & popover */}
            <div 
              className="p-4 bg-slate-50/55 hover:bg-slate-50 border border-slate-100/60 rounded-xl transition-all relative group shadow-3xs"
              onMouseEnter={() => setActiveFindingMenu("f1")}
              onMouseLeave={() => setActiveFindingMenu(null)}
            >
              <div className="flex justify-between items-start gap-4">
                <div className="space-y-2.5">
                  <div className="flex flex-wrap gap-1.5 items-center">
                    <span className="text-[8.5px] font-mono bg-rose-50 border border-rose-100/65 text-rose-800 font-extrabold uppercase px-2 py-0.5 rounded-full">
                      🔴 Critical Testimonial Contradiction Case-0042
                    </span>
                    <span className="text-[9px] font-mono text-slate-400 font-bold uppercase">FLAGGED VIA BILATERAL DISCOVERY SYNC</span>
                  </div>

                  <h5 className="font-extrabold text-slate-900 text-xs leading-snug">
                    Oral agreement extension date discrepancy [Witness Yousuf Khan] vs [Corporate Invoice ev-03].
                  </h5>
                  
                  <p className="text-slate-705 leading-relaxed text-[11.5px] font-medium">
                    The defense depositor Yousuf Khan claims a verbal payment delay and project extension was granted on 12 March, whereas the underlying signed corporate invoice ledger exhibits indicate late-April audit milestones with zero written consent. Under UAE civil codifications, written constraints override general verbal pleadings.
                  </p>
                </div>

                {/* Popover Action panel */}
                {activeFindingMenu === "f1" && (
                  <div className="absolute right-4 top-4 bg-slate-900 text-white p-3 rounded-xl shadow-xl space-y-1.5 z-20 flex flex-col font-sans border border-slate-800 w-52 animate-fade-in">
                    <span className="font-mono text-[8px] text-amber-400 block pb-1 border-b border-slate-800 uppercase tracking-widest font-black">
                      COMPLIANCE ACTIONS:
                    </span>
                    
                    <button 
                      type="button"
                      onClick={() => { 
                        writeLogs(`${role} User`, "CASE_FACT_PIN", "Overview pinned."); 
                        setActiveFindingMenu(null);
                        setPersistenceStatus(isAr ? "تم تثبيت النتيجة في مساحة العمل." : "Finding pinned to workspace."); 
                      }}
                      className="w-full text-left p-1.5 rounded-lg hover:bg-slate-800 flex items-center gap-1.5 font-bold transition-all text-[10px] cursor-pointer"
                    >
                      <Pin size={11} className="text-amber-400" /> Pin to Dashboard
                    </button>

                    <button 
                      type="button"
                      onClick={() => { 
                        writeLogs(`${role} User`, "CASE_BRIEF_ADD", "Added draft context."); 
                        setActiveFindingMenu(null);
                        setPersistenceStatus(isAr ? "تمت إضافة التباين إلى مسودة المرافعة." : "Discrepancy inserted into pleadings draft."); 
                      }}
                      className="w-full text-left p-1.5 rounded-lg hover:bg-slate-800 flex items-center gap-1.5 font-bold transition-all text-[10px] cursor-pointer"
                    >
                      <FileCheck size={11} className="text-emerald-400" /> Insert into Pleadings Draft
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Finding Item 2 */}
            <div 
              className="p-4 bg-slate-50/55 hover:bg-slate-50 border border-slate-100/60 rounded-xl transition-all relative group shadow-3xs"
              onMouseEnter={() => setActiveFindingMenu("f2")}
              onMouseLeave={() => setActiveFindingMenu(null)}
            >
              <div className="flex justify-between items-start gap-4">
                <div className="space-y-2.5">
                  <div className="flex flex-wrap gap-1.5 items-center">
                    <span className="text-[8.5px] font-mono bg-orange-50 border border-orange-100/65 text-orange-700 font-extrabold uppercase px-2 py-0.5 rounded-full">
                      🔵 Performance Milestone Discrepancy
                    </span>
                    <span className="text-[9px] font-mono text-slate-400 font-bold uppercase">FORCE MAJEURE INAPPLICABILITY</span>
                  </div>

                  <h5 className="font-extrabold text-slate-900 text-xs leading-snug">
                    Exclusion of force majeure claim for local logistics and port entry delay.
                  </h5>
                  
                  <p className="text-slate-705 leading-relaxed text-[11.5px] font-medium">
                    Defense engineering briefs cite Middle-East shipping delay bottlenecks as sovereign force majeure. However, digital analysis of the original commercial contract confirms explicit exclusions for pre-notified regional delays that were already logged on public registries.
                  </p>
                </div>

                {/* Popover Action panel */}
                {activeFindingMenu === "f2" && (
                  <div className="absolute right-4 top-4 bg-slate-900 text-white p-3 rounded-xl shadow-xl space-y-1.5 z-20 flex flex-col font-sans border border-slate-800 w-52 animate-fade-in">
                    <span className="font-mono text-[8px] text-amber-400 block pb-1 border-b border-slate-800 uppercase tracking-widest font-black">
                      COMPLIANCE ACTIONS:
                    </span>
                    
                    <button 
                      type="button"
                      onClick={() => { 
                        writeLogs(`${role} User`, "CASE_FACT_TAG", "FM exclusion tagged."); 
                        setActiveFindingMenu(null);
                        setPersistenceStatus(isAr ? "تم وسم التباين كحجة مطالبة أساسية." : "Discrepancy tagged as a primary claim argument."); 
                      }}
                      className="w-full text-left p-1.5 rounded-lg hover:bg-slate-800 flex items-center gap-1.5 font-bold transition-all text-[10px] cursor-pointer"
                    >
                      <Pin size={11} className="text-amber-400" /> Tag Claim Vector
                    </button>
                  </div>
                )}
              </div>
            </div>

          </div>

        </div>
      )}

      {/* Upload Exhibit Modal Redesign */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-slate-950/45 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in text-xs font-sans">
          <form 
            onSubmit={handleFileUploadMock} 
            className="bg-white rounded-xl shadow-lg max-w-md w-full max-h-[90dvh] overflow-y-auto p-5 space-y-4 border border-slate-100/60"
          >
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="text-sm font-display font-extrabold text-slate-900 uppercase">
                Onboard Secure Witness Statement / Exhibit File
              </h3>
              <button 
                type="button" 
                onClick={() => setShowUploadModal(false)}
                aria-label="Close upload exhibit modal"
                title="Close upload exhibit modal"
                className="p-1.5 rounded-lg border border-slate-100 text-slate-400 hover:text-slate-750 hover:bg-slate-50 transition-colors cursor-pointer"
              >
                <XComponent size={16} />
              </button>
            </div>

            <div className="space-y-4 text-slate-700">
              <div className="space-y-1.5">
                <label className="text-[10px] font-mono font-bold text-slate-500 uppercase block">EXHIBIT DOCUMENT TITLE:</label>
                <input 
                  type="text"
                  required
                  value={newDocName}
                  onChange={(e) => setNewDocName(e.target.value)}
                  placeholder="e.g. Certified General Ledger Statement 4v.pdf"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.2 text-xs text-slate-850 outline-none focus:ring-1 focus:ring-slate-900 shadow-2xs"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-mono font-bold text-slate-500 uppercase block">SOURCE FILE ATTACHMENT:</label>
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                  onChange={(event) => {
                    const selectedFile = event.target.files?.[0];
                    const fileName = selectedFile?.name ?? "";
                    setSelectedUploadFileName(fileName);
                    if (fileName && !newDocName) {
                      setNewDocName(fileName);
                    }
                  }}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.2 text-xs text-slate-700"
                />
                {selectedUploadFileName && <div className="text-[10px] font-semibold text-emerald-700">Attached file: {selectedUploadFileName}</div>}
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-mono font-bold text-slate-500 uppercase block">ASSET TYPE CLASSIFICATION:</label>
                <select 
                  value={newDocType}
                  onChange={(e) => setNewDocType(e.target.value as any)}
                  className="w-full bg-slate-50 border border-slate-205 rounded-xl px-3.5 py-2.2 text-xs text-slate-755 outline-none focus:ring-1 focus:ring-slate-900 shadow-2xs cursor-pointer font-semibold"
                >
                  <option value="Expert Report">Expert Forensics Report</option>
                  <option value="Invoice">Corporate Audit Invoice Ledger</option>
                  <option value="Witness Statement">Eyewitness Statement Statement</option>
                  <option value="Email">Internal Communications (Email Threads)</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-mono font-bold text-slate-500 uppercase block">SUBSTANTIVE TEXT EXCERPT (OPTIONAL):</label>
                <textarea 
                  value={newDocExcerpt}
                  onChange={(e) => setNewDocExcerpt(e.target.value)}
                  placeholder="Insert critical timelines or material clauses of contradiction..." 
                  className="w-full bg-slate-50 border border-slate-205 rounded-xl px-3.5 py-2 text-xs text-slate-850 outline-none focus:ring-1 focus:ring-slate-900 min-h-[95px] shadow-2xs font-semibold"
                />
              </div>
            </div>

            <div className="flex flex-col gap-2 pt-3 border-t border-slate-100 sm:flex-row sm:justify-end">
              <button 
                type="button" 
                onClick={() => setShowUploadModal(false)}
                className="w-full sm:w-auto px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-650 text-xs font-semibold rounded-xl cursor-pointer"
              >
                Cancel
              </button>
              <button 
                type="submit"
                className="w-full sm:w-auto px-4.5 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-black rounded-xl transition-all shadow-sm cursor-pointer"
              >
                Analyze & Register Exhibit
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Add Witness Drawer / Modal Redesign */}
      {showWitnessDrawer && (
        <div className="fixed inset-0 bg-slate-950/45 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in text-xs font-sans">
          <form 
            onSubmit={handleWitnessAddMock} 
            className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90dvh] overflow-y-auto p-6 space-y-4 border border-slate-202"
          >
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="text-sm font-display font-extrabold text-slate-900 uppercase flex items-center gap-1.5">
                <Users size={16} />
                <span>Onboard Witness Deposition Statement</span>
              </h3>
              <button 
                type="button" 
                onClick={() => setShowWitnessDrawer(false)}
                aria-label="Close witness onboarding modal"
                title="Close witness onboarding modal"
                className="p-1.5 rounded-lg border border-slate-100 text-slate-400 hover:text-slate-750 hover:bg-slate-50 transition-colors cursor-pointer"
              >
                <XComponent size={16} />
              </button>
            </div>

            <div className="space-y-4 text-slate-700">
              <div className="space-y-1.5">
                <label className="text-[10px] font-mono font-bold text-slate-500 uppercase block">Witness Depositor Full Legal Name:</label>
                <input 
                  type="text"
                  required
                  value={newWitName}
                  onChange={(e) => setNewWitName(e.target.value)}
                  placeholder="e.g. Amina Al-Mazrouei"
                  className="w-full bg-slate-50 border border-slate-202 rounded-xl px-3.5 py-2.2 text-xs text-slate-850 outline-none focus:ring-1 focus:ring-slate-900 shadow-2xs"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-mono font-bold text-slate-500 uppercase block">Testimonial Character/Role Classification:</label>
                <select 
                  value={newWitRole}
                  onChange={(e) => setNewWitRole(e.target.value as any)}
                  className="w-full bg-slate-50 border border-slate-202 rounded-xl px-3.5 py-2.2 text-xs text-slate-755 outline-none focus:ring-1 focus:ring-slate-900 shadow-2xs cursor-pointer font-bold"
                >
                  <option value="FACT">FACT Witness (direct observer dispute events)</option>
                  <option value="EXPERT">EXPERT Forensics Witness (technical certification panelist)</option>
                  <option value="CHARACTER">CHARACTER reference</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-mono font-bold text-slate-500 uppercase block">Opposing Affiliation Side:</label>
                <div className="grid grid-cols-3 gap-2">
                  {(["PLAINTIFF", "DEFENDANT", "NEUTRAL"] as const).map(side => (
                    <button
                      key={side}
                      type="button"
                      onClick={() => setNewWitSide(side)}
                      className={`py-2 rounded-xl text-[10px] font-mono tracking-wider font-black text-center border transition-all ${
                        newWitSide === side 
                          ? "bg-slate-900 text-white border-transparent shadow-md" 
                          : "bg-white text-slate-500 border-slate-202 hover:border-slate-350"
                      }`}
                    >
                      {side}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2 pt-3 border-t border-slate-100 sm:flex-row sm:justify-end">
              <button 
                type="button" 
                onClick={() => setShowWitnessDrawer(false)}
                className="w-full sm:w-auto px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-650 text-xs font-semibold rounded-xl cursor-pointer"
              >
                Cancel
              </button>
              <button 
                type="submit"
                className="w-full sm:w-auto px-4.5 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-black rounded-xl transition-all shadow-sm cursor-pointer"
              >
                Onboard Depositor Statement
              </button>
            </div>
          </form>
        </div>
      )}

    </div>
  );
};

// Simple visual Close X icon component to avoid conflicts
const XComponent: React.FC<{ size?: number; className?: string }> = ({ size = 16, className }) => (
  <svg 
    xmlns="http://www.w3.org/2500/svg" 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2.5" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);
